import React, { useEffect, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { adminMachineProductUseCase } from "../../../di/container";
import Table from "../../components/UI/Table";
import type { UnreportedMachineProductEntity } from "../../../domain/types/unreported-machine-product";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/UI/Button";
import { Trash2, Download } from "lucide-react";
import ConfirmMessageModal from "../../components/UI/ConfirmMessageModal";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";

const UnreportedMachineProducts: React.FC = () => {
  const navigate = useNavigate();
  const [unreportedList, setUnreportedList] = useState<
    UnreportedMachineProductEntity[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] =
    useState<UnreportedMachineProductEntity | null>(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmSubmit, setConfirmSubmit] = useState<() => void>(() => {});
  const { notifications, addItem: addNotification } = useNotifications();

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await adminMachineProductUseCase.getAllUnreported();
      setUnreportedList(data);
    } catch (error) {
      console.error("미보고 장비 목록 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRegisterProduct = (item: UnreportedMachineProductEntity) => {
    const params = new URLSearchParams();
    if (item.machineName) params.set("machineName", item.machineName);
    if (item.machineDescription)
      params.set("machineDescription", item.machineDescription);
    navigate(`/admin/machine-product/new?${params.toString()}`);
  };

  const handleDelete = (item: UnreportedMachineProductEntity) => {
    setConfirmMessage(
      `"${item.machineName}" 미등록 장비를 삭제할까요?`
    );
    setConfirmSubmit(() => {
      return () => {
        (async () => {
          try {
            await adminMachineProductUseCase.deleteUnreported(item.id);
            addNotification("삭제되었습니다.");
            await loadData();
          } catch (error) {
            addNotification("삭제에 실패했습니다.");
          }
          setConfirmMessage("");
          setConfirmSubmit(() => {});
        })();
      };
    });
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("ko-KR");
    } catch {
      return dateStr;
    }
  };

  const columnWidths = ["120px", "200px", "200px", "100px", "50px"];
  const head = [<>회사</>, <>장비명</>, <>설명</>, <>등록일시</>, <>삭제</>];

  return (
    <Pod24AdminLayout>
      {confirmMessage && (
        <ConfirmMessageModal
          message={confirmMessage}
          setMessage={setConfirmMessage}
          submitFunction={confirmSubmit}
        />
      )}
      <NotificationsList notifications={notifications} />

      <div className="mb-6">
        <h1
          className="text-[22px] font-bold text-[#222]"
          style={{ fontFamily: "Pretendard" }}
        >
          미등록 장비
        </h1>
        <p className="text-[13px] text-[#888] mt-1">
          라우터가 발견했으나 장비 OID DB에 등록되지 않은 장비 목록입니다.
        </p>
      </div>

      {/* 상세 모달 */}
      {selectedItem && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSelectedItem(null)}
          />
          <div className="relative bg-white rounded-[12px] p-6 w-full max-w-[500px] mx-4 shadow-lg">
            <h2 className="text-[16px] font-bold text-[#222] mb-4">
              미등록 장비 상세
            </h2>

            <div className="space-y-3 text-[14px]">
              <div className="flex">
                <span className="text-[#888] w-[120px] shrink-0">회사</span>
                <span className="text-[#222]">
                  {selectedItem.companyName || "-"}
                  <span className="text-[#888] ml-1">(ID: {selectedItem.companyId})</span>
                </span>
              </div>
              <div className="flex">
                <span className="text-[#888] w-[120px] shrink-0">
                  라우터 ID
                </span>
                <span className="text-[#222]">
                  {selectedItem.reportingRouterId}
                </span>
              </div>
              <div className="flex">
                <span className="text-[#888] w-[120px] shrink-0">장비명</span>
                <span className="text-[#222]">{selectedItem.machineName}</span>
              </div>
              <div className="flex">
                <span className="text-[#888] w-[120px] shrink-0">설명</span>
                <span className="text-[#222]">
                  {selectedItem.machineDescription || "-"}
                </span>
              </div>
              <div className="flex">
                <span className="text-[#888] w-[120px] shrink-0">
                  SNMP 데이터
                </span>
                <span className="text-[#222]">
                  {selectedItem.snmpRawDataViewUrl ? (
                    <span className="flex gap-3">
                      <a
                        href={selectedItem.snmpRawDataViewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#4285F4] hover:text-[#3367D6] underline"
                      >
                        웹페이지로 보기
                      </a>
                      <a
                        href={selectedItem.snmpRawDataDownloadUrl || selectedItem.snmpRawDataViewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#4285F4] hover:text-[#3367D6] underline"
                      >
                        <Download size={14} />
                        다운로드
                      </a>
                    </span>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
              <div className="flex">
                <span className="text-[#888] w-[120px] shrink-0">
                  등록일시
                </span>
                <span className="text-[#222]">
                  {formatDate(selectedItem.createdAt)}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedItem(null)}
              >
                닫기
              </Button>
              <Button
                size="sm"
                onClick={() => handleRegisterProduct(selectedItem)}
              >
                장비 등록
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-[#888] text-[14px]">
          불러오는 중...
        </div>
      ) : unreportedList.length === 0 ? (
        <div className="text-center py-10 text-[#888] text-[14px]">
          미등록 장비가 없습니다.
        </div>
      ) : (
        <div className="text-[12px]">
          <Table
            columnWidths={columnWidths}
            onRowClick={(rowIndex) => setSelectedItem(unreportedList[rowIndex])}
            tabledata={{
              head,
              body: unreportedList.map((item) => {
                const baseCols = [
                  <>{item.companyName || `ID: ${item.companyId}`}</>,
                  <>{item.machineName}</>,
                  <>{item.machineDescription || "-"}</>,
                  <>{formatDate(item.createdAt)}</>,
                ];

                baseCols.push(
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="flex justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <button
                      className="p-1.5 rounded hover:bg-red-50 text-[#F24A4A] transition-colors"
                      onClick={() => handleDelete(item)}
                      title="삭제"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );

                return baseCols;
              }),
            }}
          />
        </div>
      )}
    </Pod24AdminLayout>
  );
};

export default UnreportedMachineProducts;